// Task Template Service
// CRUD operations for task templates

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { protocolService } from './protocolService';

export class TaskTemplateService {
  constructor() {
    this.templatesCollection = collection(db, 'task_templates');
  }
  
  // ==================== CRUD OPERATIONS ====================
  
  /**
   * Create new task template
   */
  async createTemplate(templateData, userId) {
    try {
      const now = Timestamp.now();
      
      // Remove any existing id field to prevent conflicts
      const { id, ...cleanData } = templateData;
      
      const template = {
        ...cleanData,
        usageCount: 0,
        lastUsed: null,
        isActive: cleanData.isActive !== undefined ? cleanData.isActive : true,
        createdBy: userId,
        createdAt: now,
        updatedBy: userId,
        updatedAt: now
      };
      
      const docRef = await addDoc(this.templatesCollection, template);
      console.log(`✅ Created task template: ${docRef.id}`);
      return docRef.id;
    } catch (error) {
      console.error('Error creating task template:', error);
      throw error;
    }
  }
  
  /**
   * Get template by ID
   */
  async getTemplate(templateId) {
    try {
      const docRef = doc(this.templatesCollection, templateId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Remove any id field from data to prevent conflicts
        delete data.id;
        return { id: docSnap.id, ...data };
      }
      return null;
    } catch (error) {
      console.error('Error fetching task template:', error);
      throw error;
    }
  }
  
  /**
   * Get all templates with optional filters
   */
  async getAllTemplates(filters = {}) {
    try {
      let q = query(this.templatesCollection);
      
      // Apply filters
      if (filters.isActive !== undefined) {
        q = query(q, where('isActive', '==', filters.isActive));
      }
      
      if (filters.taskType) {
        q = query(q, where('taskType', '==', filters.taskType));
      }
      
      // Order by name
      q = query(q, orderBy('name', 'asc'));
      
      const snapshot = await getDocs(q);
      let templates = snapshot.docs.map(doc => {
        const data = doc.data();
        // Remove any id field from data to prevent conflicts
        delete data.id;
        return {
          id: doc.id,
          ...data
        };
      });
      
      // Apply search term filter (client-side)
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        templates = templates.filter(t => 
          t.name.toLowerCase().includes(searchLower) ||
          t.description.toLowerCase().includes(searchLower)
        );
      }
      
      return templates;
    } catch (error) {
      console.error('Error fetching task templates:', error);
      throw error;
    }
  }
  
  /**
   * Get templates by task type
   */
  async getTemplatesByType(taskType) {
    try {
      const q = query(
        this.templatesCollection,
        where('isActive', '==', true),
        where('taskType', '==', taskType),
        orderBy('name', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        // Remove any id field from data to prevent conflicts
        delete data.id;
        return {
          id: doc.id,
          ...data
        };
      });
    } catch (error) {
      console.error('Error fetching templates by type:', error);
      throw error;
    }
  }
  
  /**
   * Update task template
   */
  async updateTemplate(templateId, updates, userId) {
    try {
      const docRef = doc(this.templatesCollection, templateId);
      
      await updateDoc(docRef, {
        ...updates,
        updatedBy: userId,
        updatedAt: Timestamp.now()
      });
      
      console.log(`✅ Updated task template: ${templateId}`);
    } catch (error) {
      console.error('Error updating task template:', error);
      throw error;
    }
  }
  
  /**
   * Delete task template (soft delete)
   * Now includes dependency checking - throws error if dependencies exist
   */
  async deleteTemplate(templateId, userId, force = false) {
    // Check dependencies before deletion
    if (!force) {
      const { dependencyCheckService } = await import('./dependencyCheckService');
      const dependencyCheck = await dependencyCheckService.checkTaskTemplateDeletion(templateId);
      
      if (!dependencyCheck.canDelete && dependencyCheck.blockingDependencies.length > 0) {
        const errorMessages = dependencyCheck.blockingDependencies.map(d => d.message).join('; ');
        throw new Error(`Cannot delete task template: ${errorMessages}`);
      }
    }

    try {
      await this.updateTemplate(templateId, { isActive: false }, userId);
      console.log(`✅ Deleted task template: ${templateId}`);
    } catch (error) {
      console.error('Error deleting task template:', error);
      throw error;
    }
  }
  
  /**
   * Hard delete task template
   * WARNING: Should only be used after verifying no dependencies
   */
  async hardDeleteTemplate(templateId, force = false) {
    if (!force) {
      const { dependencyCheckService } = await import('./dependencyCheckService');
      const dependencyCheck = await dependencyCheckService.checkTaskTemplateDeletion(templateId);
      
      if (!dependencyCheck.canDelete) {
        throw new Error(`Cannot delete task template: Has ${dependencyCheck.blockingDependencies.length} blocking dependencies`);
      }
    }

    try {
      const docRef = doc(this.templatesCollection, templateId);
      await deleteDoc(docRef);
      console.log(`✅ Hard deleted task template: ${templateId}`);
    } catch (error) {
      console.error('Error hard deleting task template:', error);
      throw error;
    }
  }
  
  /**
   * Duplicate task template
   */
  async duplicateTemplate(templateId, userId) {
    try {
      const original = await this.getTemplate(templateId);
      if (!original) throw new Error('Template not found');
      
      const duplicate = {
        ...original,
        name: `${original.name} (Copy)`,
        usageCount: 0,
        lastUsed: null
      };
      
      // Remove ID and audit fields
      delete duplicate.id;
      delete duplicate.createdBy;
      delete duplicate.createdAt;
      delete duplicate.updatedBy;
      delete duplicate.updatedAt;
      
      return await this.createTemplate(duplicate, userId);
    } catch (error) {
      console.error('Error duplicating task template:', error);
      throw error;
    }
  }
  
  // ==================== TASK GENERATION ====================
  
  /**
   * Generate task instance from template
   */
  async generateTaskFromTemplate(templateId, customContext, assignedTo, assignedToName, scheduledStart, userId) {
    try {
      const template = await this.getTemplate(templateId);
      if (!template) throw new Error('Template not found');
      
      if (!template.isActive) {
        throw new Error('Template is not active');
      }
      
      const now = Timestamp.now();
      const scheduledStartTime = scheduledStart || now;
      
      // Calculate scheduled end based on template timing rules
      const durationMinutes = template.timingRules.durationUnit === 'hours'
        ? template.timingRules.estimatedDuration * 60
        : template.timingRules.estimatedDuration;
      
      const scheduledEndTime = new Date(scheduledStartTime.toMillis ? scheduledStartTime.toMillis() : scheduledStartTime.getTime());
      scheduledEndTime.setMinutes(scheduledEndTime.getMinutes() + durationMinutes);
      
      // Merge default context with custom context
      const taskContext = {
        ...template.defaultContext,
        ...customContext
      };
      
      // Build task instance data structure
      const taskData = {
        name: template.name,
        taskType: template.taskType,
        
        // Template reference
        templateId: template.id,
        templateName: template.name,
        
        // Package reference (null for now, will be set if part of package)
        packageId: null,
        packageName: null,
        sequenceOrder: null,
        
        // Trigger source
        triggeredBy: 'manual',
        triggeredByUserId: userId,
        triggeredByEvent: null,
        triggeredByWorkflow: null,
        
        // Protocols (will be populated with full protocol data)
        protocols: [],
        
        // Context
        context: taskContext,
        
        // Assignment
        assignedTo: assignedTo,
        assignedToName: assignedToName || '', // User's display name
        assignedRole: template.assignmentRules.requiredRole || '',
        assignmentType: template.assignmentRules.assignmentType,
        assignedAt: now,
        acknowledgedAt: null,
        
        // Group assignment
        assignedToGroup: template.assignmentRules.assignedToGroup || null,
        assignedToGroupName: null, // Will be populated if group-based
        
        // Scheduling
        scheduledStart: Timestamp.fromDate(scheduledStartTime.toMillis ? scheduledStartTime.toDate() : scheduledStartTime),
        scheduledEnd: Timestamp.fromDate(scheduledEndTime),
        actualStart: null,
        actualEnd: null,
        
        // Status
        status: 'assigned',  // From nomenclature
        priority: template.defaultPriority,
        urgency: template.defaultUrgency,
        
        // Execution tracking
        currentProtocolIndex: 0,
        currentStepIndex: 0,
        
        // Compliance
        complianceScore: null,
        completionQuality: null,
        deviations: [],
        
        // Risk (will be calculated)
        baseRPN: 0,
        currentRPN: 0,
        riskLevel: 'low',
        
        // Escalation (Phase 4)
        escalated: false,

        // Audit
        createdBy: userId,
        createdAt: now,
        updatedBy: userId,
        updatedAt: now
      };
      
      // Load protocol data for each protocol in template
      for (const protocolRef of template.protocols) {
        const protocol = await protocolService.getProtocol(protocolRef.protocolId);
        if (protocol) {
          taskData.protocols.push({
            protocolId: protocol.id,
            protocolName: protocol.name,
            protocolVersion: protocol.version || '1.0',
            sequenceOrder: protocolRef.sequenceOrder,
            status: 'pending',
            
            // Copy steps from protocol
            steps: protocol.steps.map(step => ({
              stepId: step.id,
              stepName: step.name,
              status: 'pending',
              startTime: null,
              endTime: null,
              duration: null,
              expectedDuration: step.estimatedDuration * 60, // Convert to seconds
              isOnTime: true,
              isComplete: false,
              isSkipped: false,
              skipReason: null,
              
              // Copy actions from step
              actions: (step.actions || []).map(action => ({
                actionId: action.id,
                actionName: action.name,
                status: 'pending',
                completed: false
              })),
              
              notes: null,
              photos: [],
              documentation: null
            })),
            
            startTime: null,
            endTime: null,
            duration: null,
            complianceScore: null
          });
        }
      }
      
      // Update template usage statistics
      await this.updateTemplate(templateId, {
        usageCount: (template.usageCount || 0) + 1,
        lastUsed: now
      }, userId);
      
      return taskData;
    } catch (error) {
      console.error('Error generating task from template:', error);
      throw error;
    }
  }
  
  // ==================== STATISTICS ====================
  
  /**
   * Get template usage statistics
   */
  async getTemplateStats(templateId) {
    try {
      const template = await this.getTemplate(templateId);
      if (!template) throw new Error('Template not found');
      
      return {
        usageCount: template.usageCount || 0,
        lastUsed: template.lastUsed,
        isActive: template.isActive,
        protocolCount: template.protocols.length,
        estimatedDuration: template.timingRules.estimatedDuration,
        durationUnit: template.timingRules.durationUnit
      };
    } catch (error) {
      console.error('Error fetching template stats:', error);
      throw error;
    }
  }
  
  /**
   * Get most used templates
   */
  async getMostUsedTemplates(limit = 10) {
    try {
      const templates = await this.getAllTemplates({ isActive: true });
      
      // Sort by usage count
      templates.sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0));
      
      return templates.slice(0, limit);
    } catch (error) {
      console.error('Error fetching most used templates:', error);
      throw error;
    }
  }
}

export const taskTemplateService = new TaskTemplateService();

